import { useState, useEffect, useCallback, useRef } from 'react';
import type L from 'leaflet';
import { logError } from '../utils/logger';
import type { LatLonPair } from '../components/Blocks/VoyagerSearchPageBlock/models/search-result';
import { environment } from 'nsdi-volto-frontend/config/environments';

export type BBoxPoint = LatLonPair;

export const DEFAULT_LEAFLET_ICON_BASE_URL =
  environment.maps.leafletMarkerIconBaseUrl;

export const configureLeafletDefaultIcons = (
  leaflet: typeof L,
  iconBaseUrl = DEFAULT_LEAFLET_ICON_BASE_URL,
): void => {
  delete (leaflet.Icon.Default.prototype as any)._getIconUrl;
  leaflet.Icon.Default.mergeOptions({
    iconRetinaUrl: `${iconBaseUrl}/marker-icon-2x.png`,
    iconUrl: `${iconBaseUrl}/marker-icon.png`,
    shadowUrl: `${iconBaseUrl}/marker-shadow.png`,
  });
};

interface UseLeafletDrawMapOptions {
  /** "lat,lng" string, default "20,0" */
  defaultCenter?: string;
  /** Default zoom level, default 2 */
  defaultZoom?: number;
  /**
   * Base URL for Leaflet marker icon PNGs (no trailing slash).
   * Defaults to the unpkg CDN. Pass a local origin path if icons are
   * served from the Plone static resource directory.
   */
  iconBaseUrl?: string;
  /** Extra options forwarded to L.map() - read once at init time. */
  mapOptions?: Record<string, any>;
  /**
   * Called after the default CREATED handler computes a bbox from the
   * drawn shape. Also called with [] on DELETED.
   * Not called when onDrawCreated is provided.
   */
  onBboxChange?: (bbox: BBoxPoint[]) => void;
  /**
   * If provided, replaces the default CREATED bbox extraction.
   * The layer has already been added to drawnItems before this is called.
   * Use this when you need to intercept the draw event (e.g. buffer dialog).
   */
  onDrawCreated?: (event: L.LeafletEvent, leaflet: typeof L) => void;
  /** Set to false to skip Leaflet loading entirely (e.g. showMap=false). */
  enabled?: boolean;
}

interface UseLeafletDrawMapResult {
  /** Callback ref - attach to the container div with ref={mapContainerRef}. */
  mapContainerRef: (node: HTMLDivElement | null) => void;
  /** The Leaflet map instance ref. */
  mapRef: React.MutableRefObject<L.Map | null>;
  /** The loaded Leaflet library, null until loaded. */
  leafletLib: typeof L | null;
  /** The FeatureGroup used for drawn shapes. */
  drawnItemsRef: React.MutableRefObject<L.FeatureGroup | null>;
  isMapReady: boolean;
  isLeafletLoading: boolean;
  /** Enable the rectangle draw handler programmatically. */
  drawRectangle: () => void;
  /** Enable the polygon draw handler programmatically. */
  drawPolygon: () => void;
  /** Clear all drawn shapes from the map. */
  clearDrawn: () => void;
}

/**
 * Shared hook that handles dynamic Leaflet + leaflet-draw loading and
 * map initialization with ESRI/OSM tile layers and a draw FeatureGroup.
 *
 * Used by VoyagerSearchBarBlock (programmatic draw buttons) and
 * MapBBoxSelector (full L.Control.Draw toolbar).
 */
export const useLeafletDrawMap = ({
  defaultCenter = '20,0',
  defaultZoom = 2,
  iconBaseUrl = DEFAULT_LEAFLET_ICON_BASE_URL,
  mapOptions = {},
  onBboxChange,
  onDrawCreated,
  enabled = true,
}: UseLeafletDrawMapOptions = {}): UseLeafletDrawMapResult => {
  const [leafletLib, setLeafletLib] = useState<typeof L | null>(null);
  const [isLeafletLoading, setIsLeafletLoading] = useState(false);
  const [isMapReady, setIsMapReady] = useState(false);
  const [mapContainerElement, setMapContainerElement] =
    useState<HTMLDivElement | null>(null);

  const loadAttemptedRef = useRef(false);
  const mapRef = useRef<L.Map | null>(null);
  const drawnItemsRef = useRef<L.FeatureGroup | null>(null);
  const drawRectangleRef = useRef<any>(null);
  const drawPolygonRef = useRef<any>(null);

  // Stable callback refs - prevents effect deps from changing on every render
  const onBboxChangeRef = useRef(onBboxChange);
  const onDrawCreatedRef = useRef(onDrawCreated);
  useEffect(() => {
    onBboxChangeRef.current = onBboxChange;
  }, [onBboxChange]);
  useEffect(() => {
    onDrawCreatedRef.current = onDrawCreated;
  }, [onDrawCreated]);

  // mapOptions is read once at init - store in a ref so it doesn't
  // cause the map init effect to re-run on every render.
  const mapOptionsRef = useRef(mapOptions);

  const mapContainerRef = useCallback((node: HTMLDivElement | null) => {
    setMapContainerElement(node);
  }, []);

  // Dynamically load Leaflet and leaflet-draw (client side only).
  useEffect(() => {
    if (typeof window === 'undefined' || !enabled) return;
    if (loadAttemptedRef.current || leafletLib) return;

    loadAttemptedRef.current = true;
    setIsLeafletLoading(true);

    const loadLeaflet = async () => {
      try {
        await Promise.all([
          import('leaflet/dist/leaflet.css'),
          import('leaflet-draw/dist/leaflet.draw.css'),
        ]);

        const leafletModule = await import('leaflet');
        const L = leafletModule.default;

        // leaflet-draw looks for L on window
        (window as any).L = L;
        await import('leaflet-draw');

        configureLeafletDefaultIcons(L, iconBaseUrl);

        if (typeof L.Draw === 'undefined') {
          throw new Error('leaflet-draw failed to attach to Leaflet');
        }

        setLeafletLib(L);
      } catch (error) {
        logError('Failed to load Leaflet:', error);
        loadAttemptedRef.current = false;
      } finally {
        setIsLeafletLoading(false);
      }
    };

    loadLeaflet();
  }, [enabled, iconBaseUrl, leafletLib]);

  // Initialize map once Leaflet is loaded and the container element is in the DOM.
  useEffect(() => {
    if (!leafletLib || !mapContainerElement || mapRef.current) return;

    const L = leafletLib;

    try {
      const [lat, lng] = defaultCenter
        .split(',')
        .map((c) => parseFloat(c.trim()));
      const centerLat = isNaN(lat) ? 20 : lat;
      const centerLng = isNaN(lng) ? 0 : lng;

      const map = L.map(mapContainerElement, mapOptionsRef.current).setView(
        [centerLat, centerLng],
        defaultZoom,
      );

      const esriLayer = L.tileLayer(environment.maps.arcgisWorldTopoUrl, {
        maxZoom: 19,
        attribution: 'Tiles &copy; Esri',
      });
      const osmLayer = L.tileLayer(environment.maps.openStreetMapTileUrl, {
        maxZoom: 19,
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      });

      esriLayer.addTo(map);
      L.control
        .layers(
          { 'ESRI World Topo Map': esriLayer, OpenStreetMap: osmLayer },
          {},
          { position: 'topright', collapsed: true },
        )
        .addTo(map);

      const drawnItems = new L.FeatureGroup();
      map.addLayer(drawnItems);
      drawnItemsRef.current = drawnItems;

      // Programmatic draw handlers (used by components that skip the toolbar)
      drawRectangleRef.current = new (L.Draw as any).Rectangle(map, {
        shapeOptions: { color: '#3388ff', weight: 2 },
      });
      drawPolygonRef.current = new (L.Draw as any).Polygon(map, {
        allowIntersection: false,
        showArea: true,
        shapeOptions: { color: '#3388ff', weight: 2 },
      });

      map.on(L.Draw.Event.CREATED, (event: L.LeafletEvent) => {
        const layer = (event as L.DrawEvents.Created).layer as any;
        // Clear any previous shape before adding the new one
        drawnItems.clearLayers();
        drawnItems.addLayer(layer);

        if (onDrawCreatedRef.current) {
          onDrawCreatedRef.current(event, L);
        } else {
          const bounds = layer.getBounds();
          const sw = bounds.getSouthWest();
          const ne = bounds.getNorthEast();
          const se = L.latLng(sw.lat, ne.lng);
          const nw = L.latLng(ne.lat, sw.lng);
          const bbox: BBoxPoint[] = [
            { longitude: sw.lng, latitude: sw.lat },
            { longitude: se.lng, latitude: se.lat },
            { longitude: ne.lng, latitude: ne.lat },
            { longitude: nw.lng, latitude: nw.lat },
          ];
          onBboxChangeRef.current?.(bbox);
        }
      });

      map.on(L.Draw.Event.DELETED, () => {
        onBboxChangeRef.current?.([]);
      });

      map.on(L.Draw.Event.DRAWSTART, () => {
        drawnItems.clearLayers();
      });

      mapRef.current = map;
      setIsMapReady(true);
    } catch (error) {
      logError('Failed to initialize map:', error);
    }

    return () => {
      if (mapRef.current) {
        try {
          mapRef.current.remove();
        } catch (e) {
          // ignore cleanup errors
        }
        mapRef.current = null;
        setIsMapReady(false);
      }
    };
  }, [leafletLib, mapContainerElement, defaultCenter, defaultZoom]);

  const drawRectangle = useCallback(() => {
    drawRectangleRef.current?.enable();
  }, []);

  const drawPolygon = useCallback(() => {
    drawPolygonRef.current?.enable();
  }, []);

  const clearDrawn = useCallback(() => {
    drawnItemsRef.current?.clearLayers();
  }, []);

  return {
    mapContainerRef,
    mapRef,
    leafletLib,
    drawnItemsRef,
    isMapReady,
    isLeafletLoading,
    drawRectangle,
    drawPolygon,
    clearDrawn,
  };
};
